import React from "react";
import { X } from "lucide-react";
import { Button } from "./Button";

// "What this is and how to use it" for Where to start. A small accessible
// modal: focus moves in on open and back to the opener on close, Tab is
// trapped, Escape and the backdrop close it, and the page doesn't scroll
// behind it.

const FOCUSABLE = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

export function WhereToStartIntro({ open, onClose }: { open: boolean; onClose: () => void }) {
  const dialogRef = React.useRef<HTMLDivElement>(null);
  const openerRef = React.useRef<HTMLElement | null>(null);
  const titleId = React.useId();
  const reduced =
    typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches === true;

  React.useEffect(() => {
    if (!open) return;
    openerRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    dialogRef.current?.focus();
    return () => {
      document.body.style.overflow = prevOverflow;
      const el = openerRef.current;
      if (el && document.contains(el)) el.focus();
    };
  }, [open]);

  if (!open) return null;

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      e.stopPropagation();
      onClose();
      return;
    }
    if (e.key !== "Tab") return;
    const nodes = Array.from(dialogRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE) ?? []).filter(
      (n) => !n.hasAttribute("disabled"),
    );
    const first = nodes[0];
    const last = nodes[nodes.length - 1];
    if (!first || !last) return;
    const active = document.activeElement;
    if (e.shiftKey && (active === first || active === dialogRef.current)) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && active === last) {
      e.preventDefault();
      first.focus();
    }
  };

  const steps = [
    "Pick the lists you're working on. Only hills you haven't collected count.",
    "Choose a place, or leave it on Anywhere, and say how big a day you have in mind.",
    "Atlas ranks stations and car parks by how many of those hills sit on one side of them. Pick one, swing the area round the start, and see the hills by distance.",
  ];

  return (
    <div
      className="atlas-glass"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 200,
        display: "grid",
        placeItems: "center",
        padding: 16,
        border: 0,
        background: "color-mix(in srgb, var(--bg-base) 70%, transparent)",
        animation: reduced ? undefined : "wts-intro-fade 0.18s ease-out",
      }}
    >
      <style>{`@keyframes wts-intro-fade{from{opacity:0}to{opacity:1}}`}</style>
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        onKeyDown={onKeyDown}
        style={{
          position: "relative",
          width: "100%",
          maxWidth: 440,
          maxHeight: "100%",
          overflowY: "auto",
          boxSizing: "border-box",
          padding: 24,
          background: "var(--surface-card)",
          border: "1px solid var(--border-subtle)",
          borderRadius: "var(--radius-lg)",
          boxShadow: "var(--shadow-lg)",
          display: "flex",
          flexDirection: "column",
          gap: 16,
          outline: "none",
        }}
      >
        <button
          type="button"
          aria-label="Close"
          onClick={onClose}
          style={{ position: "absolute", top: 12, right: 12, width: 32, height: 32, display: "grid", placeItems: "center", border: 0, borderRadius: 8, background: "transparent", color: "var(--text-secondary)", cursor: "pointer" }}
        >
          <X size={18} aria-hidden="true" />
        </button>
        <div style={{ display: "flex", flexDirection: "column", gap: 4, paddingRight: 32 }}>
          <span className="eyebrow">Toolkit</span>
          <h2 id={titleId} style={{ margin: 0, font: "600 22px var(--font-display)", color: "var(--text-primary)" }}>
            Where to start
          </h2>
        </div>
        <ol style={{ margin: 0, padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 12 }}>
          {steps.map((t, i) => (
            <li key={i} style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
              <span
                aria-hidden="true"
                className="tnum"
                style={{ flex: "none", width: 24, height: 24, display: "grid", placeItems: "center", borderRadius: "50%", background: "var(--surface-sunken)", border: "1px solid var(--border-default)", color: "var(--text-secondary)", font: "700 12px var(--font-mono)" }}
              >
                {i + 1}
              </span>
              <span style={{ fontSize: 14, lineHeight: 1.55, color: "var(--text-secondary)" }}>{t}</span>
            </li>
          ))}
        </ol>
        <p style={{ margin: 0, fontSize: 13, lineHeight: 1.55, color: "var(--text-muted)" }}>
          Atlas never joins the hills up or draws the route. Plan it in your usual app, then use Check a route to see what it would collect.
        </p>
        <Button block onClick={onClose}>
          Got it
        </Button>
      </div>
    </div>
  );
}
